'use client'

import { useCallback, useState } from 'react'

import { aggregateCartLines } from '@cart/_utils/cart-price-aggregates'

import type { CartProduct } from '@/app/[locale]/order/_context/order-flow-context'


/** Per-line payload forwarded to `reportPrice` (payable total + discount portion + display name). */
export type PriceInfo = { total: number; savings: number; name: string }

/**
 * Aggregates `CartItemDisplay` price callbacks into one map and derived totals.
 *
 * @param cartProducts - Current cart; `pricesLoaded` is true when the map has one entry per line.
 * @returns Map, stable `reportPrice`, flags, and reduced `totalAll` / `savingsAll`.
 */
export function useCartPrices(cartProducts: CartProduct[]) {
	const [productPrices, setProductPrices] = useState<Record<string, PriceInfo>>({})

	const reportPrice = useCallback((key: string, info: PriceInfo) => {
		setProductPrices(prev => {
			if (prev[key]?.total === info.total && prev[key]?.savings === info.savings) return prev
			return { ...prev, [key]: info }
		})
	}, [])

	const pricesLoaded = Object.keys(productPrices).length === cartProducts.length
	const showSummary = pricesLoaded
	const { totalAll, savingsAll } = aggregateCartLines(productPrices)

	return { productPrices, reportPrice, pricesLoaded, showSummary, totalAll, savingsAll }
}
